import { Link } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";

const EFFECTIVE_DATE = "May 13, 2026";

export default function TermsOfService() {
  const { theme } = useTheme();

  return (
    <div
      className="min-h-screen px-4 py-12"
      style={{
        background: theme.appBg || 'linear-gradient(135deg, #0ea5e9, #0f766e)',
        color: theme.textColor,
      }}
    >
      <article className="max-w-3xl mx-auto" style={{ lineHeight: 1.6 }}>
        <header className="mb-10">
          <h1 className="text-4xl font-bold mb-2">EarthSocial Terms of Service</h1>
          <p style={{ opacity: 0.7, fontSize: '14px' }}>
            Effective {EFFECTIVE_DATE}. These Terms apply only to EarthSocial; your{" "}
            Earth Servers identity (your Company account) is governed by separate Company terms.
          </p>
        </header>

        <Section title="1. Acceptance of these Terms">
          <p>
            By creating an EarthSocial profile, you agree to these Terms of Service ("Terms").
            If you don't agree, don't claim a username — you can still use your Earth Servers
            (Company) account elsewhere in the ecosystem without joining EarthSocial.
          </p>
          <p>
            "EarthSocial," "we," "us," or "our" refers to the EarthSocial service operated by
            Earth Servers ("Operator"). "You" or "your" refers to the person using EarthSocial
            under a valid Earth Servers identity.
          </p>
        </Section>

        <Section title="2. The service">
          <p>
            EarthSocial is a social platform within the Earth Servers ecosystem. It provides
            user profiles, posts, comments, communities, direct messages, a marketplace, and
            related social features. EarthSocial is distinct from Company — Company handles
            account creation, authentication, and your underlying Earth Servers identity;
            EarthSocial is the social layer built on top of that identity.
          </p>
        </Section>

        <Section title="3. Eligibility">
          <p>
            You must be at least 13 years old (or the age of digital consent in your
            jurisdiction, whichever is higher) to use EarthSocial. If you are under 18, you
            may use the service only with the involvement of a parent or legal guardian.
            EarthSocial is not directed at children under 13, and we don't knowingly collect
            information from them.
          </p>
        </Section>

        <Section title="4. Your account and identity">
          <p>
            EarthSocial accounts are not standalone — they are tied to your Earth Servers
            (Company) identity. By signing in to EarthSocial, you authorize us to read a
            minimal profile from Company (your stable user id, username, discriminator,
            optional display name, and avatar reference) so we can identify you across the
            ecosystem.
          </p>
          <p>
            Your EarthSocial username is separate from your Company username. You choose it
            when you first sign in. We may refuse usernames that impersonate others, contain
            slurs or illegal content, or that we believe in good faith are being squatted or
            abused. You may change your username later in Settings, subject to availability.
          </p>
          <p>
            You're responsible for keeping your Company account secure (Company's terms cover
            that). If your Company account is compromised, your EarthSocial profile is
            compromised too. Notify us promptly if you suspect unauthorized use.
          </p>
        </Section>

        <Section title="5. Your content">
          <p>
            You retain ownership of everything you post — text, images, video, audio,
            marketplace listings, profile fields, anything. By posting content to EarthSocial,
            you grant Operator a worldwide, non-exclusive, royalty-free license to host,
            store, reproduce, modify (for technical purposes like resizing or transcoding),
            display, and distribute that content as needed to operate and improve the
            service. This license ends when you delete the content, except to the extent we
            need to retain it for legal compliance, dispute resolution, or backups (see
            §13).
          </p>
          <p>
            You represent that you have the right to grant this license for everything you
            post — that you either own it or have permission from the owner.
          </p>
        </Section>

        <Section title="6. Acceptable use">
          <p>You agree not to use EarthSocial to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              Post content that is illegal where you live, where we operate, or where the
              content is hosted.
            </li>
            <li>
              Harass, threaten, dox, or stalk other users; coordinate or incite real-world
              violence; or publish private information about someone without consent.
            </li>
            <li>
              Post content that sexualizes minors, glorifies self-harm, or promotes terrorism
              or organized hate.
            </li>
            <li>
              Infringe on intellectual property — don't post things you don't have the right
              to share. See §11 for our copyright takedown process.
            </li>
            <li>
              Impersonate any person, group, or service (including Earth Servers staff).
            </li>
            <li>
              Send spam, run unauthorized automated activity, scrape pages at high volume, or
              try to circumvent rate limits or moderation tools.
            </li>
            <li>
              Distribute malware, exploits, phishing pages, or anything designed to harm
              other users or the platform.
            </li>
            <li>
              Reverse-engineer the service to extract data we don't make public, or attempt
              to access accounts or content that aren't yours.
            </li>
          </ul>
          <p>
            Each community on EarthSocial may set additional rules. Community moderators
            enforce those rules within their community; site-wide moderators enforce these
            Terms across the entire platform.
          </p>
        </Section>

        <Section title="7. Communities and moderation">
          <p>
            Anyone can create a community on EarthSocial. Community owners and moderators set
            their own rules and decide who can join. They have tools to remove content,
            suspend members, and configure visibility. Earth Servers staff may intervene if a
            community is being used to violate these Terms.
          </p>
          <p>
            We may remove any content or restrict any account that we reasonably believe
            violates these Terms or applicable law. We try to be transparent about why, but
            sometimes — especially in safety or legal cases — we can't share details.
          </p>
        </Section>

        <Section title="8. Marketplace">
          <p>
            EarthSocial includes a marketplace where users can list digital assets for sale.
            Marketplace activity is a transaction between the seller and the buyer; Earth
            Servers facilitates it but is not a party to the sale. Sellers are responsible for
            their listings and any tax or legal obligations that come with selling. Buyers are
            responsible for confirming they want what they're buying.
          </p>
          <p>
            We charge a platform fee (currently 30%) on completed transactions, deducted at
            the point of sale. Fees may change with notice. Refunds and disputes are subject
            to the marketplace policy linked in your seller dashboard; we may reverse
            payments where required by law or in clear cases of fraud.
          </p>
          <p>
            Prohibited listings include anything illegal where it's being sold or shipped,
            stolen goods or stolen accounts, anything that infringes third-party rights, and
            anything that violates §6.
          </p>
        </Section>

        <Section title="9. Suspension and termination">
          <p>
            You can delete your EarthSocial profile at any time from Settings. Your Company
            account is unaffected — you'll still have your Earth Servers identity for other
            services. Some content (DMs sent to others, marketplace transaction records, log
            data) may persist as described in our Privacy Policy.
          </p>
          <p>
            We may suspend or terminate your EarthSocial profile if you violate these Terms,
            if your account is implicated in fraud or abuse, or if we're required to do so by
            law. We'll usually notify you and give you a chance to respond, but in urgent or
            legally sensitive cases we may act first.
          </p>
        </Section>

        <Section title="10. Disclaimers">
          <p>
            EarthSocial is provided "as is" and "as available." We make no warranties that the
            service will be uninterrupted, error-free, secure, or fit for any particular
            purpose. User-generated content is the responsibility of the user who posted it;
            we don't endorse it just because we host it.
          </p>
        </Section>

        <Section title="11. Copyright takedowns (DMCA)">
          <p>
            If you believe content on EarthSocial infringes your copyright, send a written
            notice to{" "}
            <a
              href="mailto:[admin@earthservers.net]"
              className="underline"
              style={{ color: theme.textColor }}
            >
              [admin@earthservers.net]
            </a>{" "}
            including: identification of the work, the URL of the allegedly infringing
            material, your contact information, a statement of good-faith belief that the use
            is unauthorized, a statement under penalty of perjury that the information is
            accurate and you're authorized to act, and your physical or electronic signature.
            Repeat infringers will have their accounts terminated.
          </p>
        </Section>

        <Section title="12. Limitation of liability">
          <p>
            To the maximum extent permitted by law, Earth Servers and its operators are not
            liable for indirect, incidental, special, consequential, or punitive damages, or
            any loss of profits, revenue, data, or goodwill, arising out of your use of
            EarthSocial. Our total liability for any claim is limited to the amount you've
            paid us in the past twelve months, or US$100, whichever is greater.
          </p>
        </Section>

        <Section title="13. Changes to these Terms">
          <p>
            We may update these Terms occasionally. For material changes, we'll notify you in
            advance — typically via an in-app notice or email — and give you a reasonable
            chance to review them before they take effect. Continuing to use EarthSocial after
            the effective date means you accept the updated Terms.
          </p>
        </Section>

        <Section title="14. Governing law and disputes">
          <p>
            These Terms are governed by the laws of [Jurisdiction], without regard to its
            conflict-of-laws rules. You and Earth Servers agree to first try to resolve any
            dispute informally by contacting{" "}
            <a
              href="mailto:[legal@earthservers.net]"
              className="underline"
              style={{ color: theme.textColor }}
            >
              [legal@earthservers.net]
            </a>
            . If we can't resolve it within 60 days, the dispute will be resolved in the
            courts of Kamloops, British Columbia, Canada, except that either party may seek injunctive relief in
            any court of competent jurisdiction to protect intellectual property or stop
            ongoing harm.
          </p>
        </Section>

        <Section title="15. Contact">
          <p>
            Questions about these Terms? Contact us at{" "}
            <a
              href="mailto:admin@earthservers.net"
              className="underline"
              style={{ color: theme.textColor }}
            >
              admin@earthservers.net
            </a>
            .
          </p>
        </Section>

        <footer className="mt-12 pt-6" style={{ borderTop: `1px solid ${theme.textColor}22` }}>
          <Link
            to="/login"
            className="inline-block px-6 py-3 rounded-lg font-semibold"
            style={{
              backgroundColor: theme.accentColor,
              color: '#1a1a1a',
            }}
          >
            Back to Login
          </Link>
        </footer>
      </article>
    </div>
  );
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <section className="mb-8">
      <h2 className="text-2xl font-semibold mb-3">{title}</h2>
      <div className="space-y-3" style={{ opacity: 0.92 }}>
        {children}
      </div>
    </section>
  );
}
